import React, { useEffect, useState } from "react";
import { AuthorLinkProps } from "@/types/modules/author/components";
import Link from "next/link";
import { ImageWithSkeleton } from "@/components/ui-kit/ImageWithSkeleton";
import useSettingsProvider from "@/core/dataProviders/providers/app/useSettingsProvider";
import { useOfflineImageContent } from "@/hooks/indexeddb/useOfflineImageContent";

interface AuthorImageSources {
  [key: number]: string;
}

export const AuthorLink: React.FC<AuthorLinkProps> = ({ authors, className }) => {
  const [authorImageSources, setAuthorImageSources] = useState<AuthorImageSources>({});
  const [settings] = useSettingsProvider();
  const { loadOfflineImage } = useOfflineImageContent();

  useEffect(() => {
    const authorImageSources: AuthorImageSources = {};
    authors?.forEach((author) => {
      if (author.image?.url) {
        loadOfflineImage(author.image.url).then((res) => {
          authorImageSources[author.id] = res ?? author.image?.url!;
          setAuthorImageSources({ ...authorImageSources });
        });
      }
    });
  }, [authors, loadOfflineImage]);

  return (
    <div className="flex items-start">
      <div className={`isolate mr-[12px] flex items-center -space-x-2`}>
        {authors?.map((author, key) => (
          <Link
            href={author.link}
            key={key}
            className="flex h-[38px] w-[38px] items-center justify-center overflow-hidden rounded-full border-2 border-white dark:border-dark"
            style={{
              zIndex: authors.length - key,
            }}
          >
            <ImageWithSkeleton
              src={authorImageSources[author.id] ?? settings?.authorFallbackImage ?? ""}
              alt={`${author?.name} - ${author?.jobTitle}`}
              width={38}
              height={38}
              className="object-cover"
            />
          </Link>
        ))}
      </div>
      <div className="mt-1.5 flex flex-wrap">
        {authors?.map((author, key) => (
          <Link key={key} href={author.link} className={`${className}`}>
            <span className="font-condensed text-md font-light sm:text-lg">{author.name}</span>
            <span className="mr-1">{key + 1 !== authors.length ? "," : ""}</span>
          </Link>
        ))}
      </div>
    </div>
  );
};
